import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import {
  ArrowLeft,
  AtSign,
  BellRing,
  Check,
  ChevronDown,
  FileText,
  Hash,
  Image,
  LogOut,
  MessageCircleMore,
  MessageSquarePlus,
  MoreHorizontal,
  Paperclip,
  Plus,
  Search,
  Send,
  Settings,
  Smile,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { supabase } from './supabase';
import { chatRuntimeConfig } from './runtime';

type ManualStatus = 'available' | 'busy' | 'away' | 'appear_offline';
type ConversationKind = 'direct' | 'group' | 'location';

type Tenant = {
  id: string;
  name: string;
  slug: string;
  role: string;
};

type DirectoryPerson = {
  tenant_id: string;
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  primary_location: string | null;
  active: boolean;
};

type ChatMember = {
  conversation_id: string;
  tenant_id: string;
  user_id: string;
  role: string;
  joined_at: string;
  last_read_at: string | null;
  muted_until: string | null;
  pinned_at: string | null;
  archived_at: string | null;
};

type ConversationRow = {
  id: string;
  tenant_id: string;
  kind: ConversationKind;
  title: string | null;
  location_key: string | null;
  direct_key: string | null;
  avatar_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  archived_at: string | null;
};

type MessageRow = {
  id: string;
  conversation_id: string;
  tenant_id: string;
  sender_id: string;
  body: string;
  message_type: 'text' | 'attachment' | 'nudge' | 'system';
  reply_to_id: string | null;
  record_type: string | null;
  record_id: string | null;
  record_label: string | null;
  record_path: string | null;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
};

type AttachmentRow = {
  id: string;
  message_id: string;
  conversation_id: string;
  tenant_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploader_id: string;
  created_at: string;
};

type ReactionRow = {
  id: string;
  message_id: string;
  tenant_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
};

type ConversationSummary = ConversationRow & {
  displayName: string;
  initials: string;
  otherUserId: string | null;
  members: ChatMember[];
  lastMessage: MessageRow | null;
  unreadCount: number;
  myMembership: ChatMember;
};

type PresenceEntry = {
  user_id?: string;
  status?: ManualStatus;
};

const STATUS_LABEL: Record<ManualStatus, string> = {
  available: 'Available',
  busy: 'Busy',
  away: 'Away',
  appear_offline: 'Appear offline',
};

function initials(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'PF';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function timeLabel(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' }).format(date);
  }
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' }).format(date);
}

function dayLabel(value: string) {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
}

function safeFileName(name: string) {
  return (name.replace(/[^a-zA-Z0-9._-]+/g, '_') || 'attachment').slice(-140);
}

function AuthScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const signIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setMessage(error.message);
  };

  const resetPassword = async () => {
    if (!email.trim()) {
      setMessage('Enter your PowderFlow email first.');
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    });
    setBusy(false);
    setMessage(error ? error.message : 'Password reset email sent.');
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="auth-brand">
          <div className="brand-mark">PF</div>
          <div>
            <strong>PowderFlow</strong>
            <span>Chat</span>
          </div>
        </div>
        <div className="auth-copy">
          <span className="eyebrow">INSTANT MESSAGING</span>
          <h1>Your PowderFlow people, one message away.</h1>
          <p>Use the same PowderFlow account you already use at work.</p>
        </div>
        <form onSubmit={signIn} className="auth-form">
          <label>
            Email
            <input
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </label>
          {message && <div className="auth-message">{message}</div>}
          <button className="primary-button auth-submit" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <button className="link-button" type="button" onClick={resetPassword} disabled={busy}>
            Forgot password?
          </button>
        </form>
        <div className="auth-foot">
          <Zap size={15} />
          <span>No separate chat account. No Floot runtime dependency.</span>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [directory, setDirectory] = useState<DirectoryPerson[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState('');
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [reactions, setReactions] = useState<ReactionRow[]>([]);
  const [presence, setPresence] = useState<Record<string, ManualStatus>>({});
  const [manualStatus, setManualStatus] = useState<ManualStatus>('available');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [groupMode, setGroupMode] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [mobileConversationOpen, setMobileConversationOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [nudgePulse, setNudgePulse] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const presenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const activeTenant = tenants.find((tenant) => tenant.id === tenantId) ?? null;
  const personMap = useMemo(
    () => new Map(directory.map((person) => [person.user_id, person])),
    [directory],
  );

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(data.session?.user ?? null);
      setAuthReady(true);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthReady(true);
    });
    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const loadTenants = useCallback(async () => {
    if (!user) return;
    const { data: memberships, error: membershipError } = await supabase
      .from('tenant_memberships')
      .select('tenant_id,membership_role')
      .eq('user_id', user.id);
    if (membershipError) throw membershipError;
    const ids = (memberships ?? []).map((row) => row.tenant_id);
    if (ids.length === 0) {
      setTenants([]);
      setTenantId('');
      return;
    }

    const { data: tenantRows, error: tenantError } = await supabase
      .from('tenants')
      .select('id,name,slug')
      .in('id', ids);
    if (tenantError) throw tenantError;

    const roleMap = new Map((memberships ?? []).map((row) => [row.tenant_id, row.membership_role]));
    const nextTenants = (tenantRows ?? []).map((tenant) => ({
      ...tenant,
      role: roleMap.get(tenant.id) ?? 'member',
    })) as Tenant[];
    setTenants(nextTenants);

    const remembered = localStorage.getItem('pfchat:tenant');
    const nextId =
      (remembered && nextTenants.some((tenant) => tenant.id === remembered) && remembered) ||
      nextTenants[0]?.id ||
      '';
    setTenantId((current) =>
      current && nextTenants.some((tenant) => tenant.id === current) ? current : nextId,
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setTenants([]);
      setTenantId('');
      return;
    }
    loadTenants().catch((err: Error) => setError(err.message));
  }, [user, loadTenants]);

  useEffect(() => {
    if (tenantId) localStorage.setItem('pfchat:tenant', tenantId);
  }, [tenantId]);

  const loadDirectory = useCallback(async () => {
    if (!tenantId) return;
    const { data, error: directoryError } = await supabase
      .from('chat_directory')
      .select('tenant_id,user_id,display_name,avatar_url,primary_location,active')
      .eq('tenant_id', tenantId)
      .eq('active', true)
      .order('display_name');
    if (directoryError) throw directoryError;
    setDirectory((data ?? []) as DirectoryPerson[]);
  }, [tenantId]);

  const loadPresencePreference = useCallback(async () => {
    if (!user || !tenantId) return;
    const { data, error: preferenceError } = await supabase
      .from('chat_presence_preferences')
      .select('manual_status')
      .eq('user_id', user.id)
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (preferenceError) throw preferenceError;
    setManualStatus((data?.manual_status as ManualStatus | undefined) ?? 'available');
  }, [tenantId, user]);

  const loadConversations = useCallback(async () => {
    if (!user || !tenantId) return;

    const { data: myRows, error: membershipError } = await supabase
      .from('chat_members')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('user_id', user.id)
      .is('archived_at', null);
    if (membershipError) throw membershipError;

    const myMemberships = (myRows ?? []) as ChatMember[];
    const ids = myMemberships.map((row) => row.conversation_id);
    if (ids.length === 0) {
      setConversations([]);
      setActiveConversationId('');
      return;
    }

    const [{ data: conversationRows, error: conversationError }, { data: allMemberRows, error: allMemberError }] =
      await Promise.all([
        supabase
          .from('chat_conversations')
          .select('*')
          .in('id', ids)
          .is('archived_at', null)
          .order('last_message_at', { ascending: false, nullsFirst: false }),
        supabase.from('chat_members').select('*').in('conversation_id', ids),
      ]);
    if (conversationError) throw conversationError;
    if (allMemberError) throw allMemberError;

    const rows = (conversationRows ?? []) as ConversationRow[];
    const allMembers = (allMemberRows ?? []) as ChatMember[];
    const myMap = new Map(myMemberships.map((membership) => [membership.conversation_id, membership]));

    const summaries = await Promise.all(
      rows.map(async (conversation): Promise<ConversationSummary> => {
        const members = allMembers.filter((member) => member.conversation_id === conversation.id);
        const myMembership = myMap.get(conversation.id)!;
        const other = members.find((member) => member.user_id !== user.id) ?? null;
        const otherPerson = other ? personMap.get(other.user_id) : null;
        const displayName =
          conversation.kind === 'direct'
            ? otherPerson?.display_name ?? 'Direct chat'
            : conversation.title ?? (conversation.kind === 'location' ? 'Location chat' : 'Group chat');

        const { data: latest } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', conversation.id)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        let unreadQuery = supabase
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('conversation_id', conversation.id)
          .neq('sender_id', user.id)
          .is('deleted_at', null);
        if (myMembership.last_read_at) unreadQuery = unreadQuery.gt('created_at', myMembership.last_read_at);
        const { count } = await unreadQuery;

        return {
          ...conversation,
          displayName,
          initials: initials(displayName),
          otherUserId: other?.user_id ?? null,
          members,
          lastMessage: (latest as MessageRow | null) ?? null,
          unreadCount: count ?? 0,
          myMembership,
        };
      }),
    );

    summaries.sort((a, b) => {
      const pinned = Number(Boolean(b.myMembership.pinned_at)) - Number(Boolean(a.myMembership.pinned_at));
      if (pinned) return pinned;
      return new Date(b.last_message_at ?? b.created_at).getTime() - new Date(a.last_message_at ?? a.created_at).getTime();
    });

    setConversations(summaries);
    setActiveConversationId((current) =>
      current && summaries.some((conversation) => conversation.id === current)
        ? current
        : summaries[0]?.id ?? '',
    );
  }, [personMap, tenantId, user]);

  const loadMessages = useCallback(async () => {
    if (!activeConversationId || !user) {
      setMessages([]);
      setAttachments([]);
      setReactions([]);
      return;
    }

    const [messageResponse, attachmentResponse] = await Promise.all([
      supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', activeConversationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true }),
      supabase.from('chat_attachments').select('*').eq('conversation_id', activeConversationId),
    ]);

    if (messageResponse.error) throw messageResponse.error;
    if (attachmentResponse.error) throw attachmentResponse.error;

    const nextMessages = (messageResponse.data ?? []) as MessageRow[];
    setMessages(nextMessages);
    setAttachments((attachmentResponse.data ?? []) as AttachmentRow[]);

    if (nextMessages.length > 0) {
      const { data: nextReactions, error: nextReactionError } = await supabase
        .from('chat_reactions')
        .select('*')
        .in('message_id', nextMessages.map((message) => message.id));
      if (nextReactionError) throw nextReactionError;
      setReactions((nextReactions ?? []) as ReactionRow[]);
    } else {
      setReactions([]);
    }


    await supabase
      .from('chat_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('conversation_id', activeConversationId)
      .eq('user_id', user.id);
  }, [activeConversationId, user]);

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([loadDirectory(), loadPresencePreference()]).catch((err: Error) => setError(err.message));
  }, [tenantId, loadDirectory, loadPresencePreference]);

  useEffect(() => {
    if (!tenantId || directory.length === 0) return;
    loadConversations().catch((err: Error) => setError(err.message));
  }, [directory, loadConversations, tenantId]);

  useEffect(() => {
    loadMessages().catch((err: Error) => setError(err.message));
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  useEffect(() => {
    if (!user) return;
    const userChannel = supabase
      .channel(`user:${user.id}:chat`, { config: { private: true } })
      .on('broadcast', { event: 'message' }, () => {
        loadConversations().catch(console.error);
      })
      .on('broadcast', { event: 'membership' }, () => {
        loadConversations().catch(console.error);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(userChannel);
    };
  }, [loadConversations, user]);

  useEffect(() => {
    if (!activeConversationId) return;
    const channel = supabase
      .channel(`chat:${activeConversationId}`, { config: { private: true } })
      .on('broadcast', { event: 'INSERT' }, (payload) => {
        const record = payload.payload?.record as MessageRow | undefined;
        if (record?.message_type === 'nudge' && record.sender_id !== user?.id) {
          setNudgePulse(true);
          window.setTimeout(() => setNudgePulse(false), 900);
        }
        loadMessages().catch(console.error);
        loadConversations().catch(console.error);
      })
      .on('broadcast', { event: 'UPDATE' }, () => {
        loadMessages().catch(console.error);
        loadConversations().catch(console.error);
      })
      .on('broadcast', { event: 'DELETE' }, () => {
        loadMessages().catch(console.error);
        loadConversations().catch(console.error);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeConversationId, loadConversations, loadMessages, user?.id]);

  useEffect(() => {
    if (!tenantId || !user) return;

    const channel = supabase
      .channel(`tenant:${tenantId}:presence`, {
        config: { private: true, presence: { key: user.id } },
      })
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<PresenceEntry>();
        const next: Record<string, ManualStatus> = {};
        Object.values(state).flat().forEach((entry) => {
          if (entry.user_id && entry.status) next[entry.user_id] = entry.status;
        });
        setPresence(next);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && manualStatus !== 'appear_offline') {
          await channel.track({
            user_id: user.id,
            status: manualStatus,
            online_at: new Date().toISOString(),
          });
        }
      });

    presenceChannelRef.current = channel;
    return () => {
      presenceChannelRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [tenantId, user]);

  useEffect(() => {
    const channel = presenceChannelRef.current;
    if (!channel || !user) return;
    if (manualStatus === 'appear_offline') {
      channel.untrack();
    } else {
      channel.track({
        user_id: user.id,
        status: manualStatus,
        online_at: new Date().toISOString(),
      });
    }
  }, [manualStatus, user]);

  const changeStatus = async (status: ManualStatus) => {
    if (!user || !tenantId) return;
    setManualStatus(status);
    const { error: statusError } = await supabase
      .from('chat_presence_preferences')
      .upsert(
        {
          user_id: user.id,
          tenant_id: tenantId,
          manual_status: status,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' },
      );
    if (statusError) setError(statusError.message);
  };

  const activeConversation =
    conversations.find((conversation) => conversation.id === activeConversationId) ?? null;

  const filteredConversations = conversations.filter((conversation) => {
    const haystack = [
      conversation.displayName,
      conversation.lastMessage?.body ?? '',
      conversation.location_key ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  const filteredPeople = directory.filter(
    (person) =>
      person.user_id !== user?.id &&
      person.display_name.toLowerCase().includes(search.toLowerCase()),
  );

  const sendMessage = async (type: MessageRow['message_type'] = 'text') => {
    if (!user || !tenantId || !activeConversationId) return;
    const body = draft.trim();
    if (type === 'text' && !body) return;
    setSending(true);
    setError('');
    const { error: sendError } = await supabase.from('chat_messages').insert({
      conversation_id: activeConversationId,
      tenant_id: tenantId,
      sender_id: user.id,
      body: type === 'nudge' ? '' : body,
      message_type: type,
    });
    setSending(false);
    if (sendError) {
      setError(sendError.message);
      return;
    }
    setDraft('');
    await loadMessages();
    await loadConversations();
  };

  const uploadAttachment = async (file: File) => {
    if (!user || !tenantId || !activeConversationId) return;
    if (file.size > 25 * 1024 * 1024) {
      setError('Attachments are limited to 25 MB.');
      return;
    }
    setSending(true);
    setError('');
    const messageId = crypto.randomUUID();
    const path = `${tenantId}/${activeConversationId}/${messageId}/${crypto.randomUUID()}-${safeFileName(file.name)}`;

    const { error: uploadError } = await supabase.storage
      .from('chat-attachments')
      .upload(path, file, { contentType: file.type || undefined, upsert: false });
    if (uploadError) {
      setSending(false);
      setError(uploadError.message);
      return;
    }

    const { error: messageError } = await supabase.from('chat_messages').insert({
      id: messageId,
      conversation_id: activeConversationId,
      tenant_id: tenantId,
      sender_id: user.id,
      body: draft.trim(),
      message_type: 'attachment',
    });
    if (messageError) {
      await supabase.storage.from('chat-attachments').remove([path]);
      setSending(false);
      setError(messageError.message);
      return;
    }

    const { error: metadataError } = await supabase.from('chat_attachments').insert({
      message_id: messageId,
      conversation_id: activeConversationId,
      tenant_id: tenantId,
      storage_path: path,
      file_name: file.name,
      mime_type: file.type || null,
      size_bytes: file.size,
      uploader_id: user.id,
    });
    if (metadataError) {
      await supabase.storage.from('chat-attachments').remove([path]);
      setError(metadataError.message);
    }

    setDraft('');
    setSending(false);
    await loadMessages();
    await loadConversations();
  };

  const openAttachment = async (attachment: AttachmentRow) => {
    const { data, error: signedError } = await supabase.storage
      .from('chat-attachments')
      .createSignedUrl(attachment.storage_path, 60);
    if (signedError) {
      setError(signedError.message);
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const reactTo = async (messageId: string, emoji: string) => {
    if (!user || !tenantId) return;
    const existing = reactions.find(
      (reaction) => reaction.message_id === messageId && reaction.user_id === user.id && reaction.emoji === emoji,
    );
    const response = existing
      ? await supabase.from('chat_reactions').delete().eq('id', existing.id)
      : await supabase.from('chat_reactions').insert({
          message_id: messageId,
          tenant_id: tenantId,
          user_id: user.id,
          emoji,
        });
    if (response.error) setError(response.error.message);
    else loadMessages().catch(console.error);
  };

  const startDirect = async (personId: string) => {
    if (!tenantId) return;
    setError('');
    const { data, error: directError } = await supabase.rpc('chat_get_or_create_direct', {
      p_tenant_id: tenantId,
      p_other_user_id: personId,
    });
    if (directError) {
      setError(directError.message);
      return;
    }
    setNewChatOpen(false);
    setSearch('');
    await loadConversations();
    if (typeof data === 'string') {
      setActiveConversationId(data);
      setMobileConversationOpen(true);
    }
  };

  const createGroup = async () => {
    if (!tenantId || !groupTitle.trim()) return;
    const { data, error: groupError } = await supabase.rpc('chat_create_group', {
      p_tenant_id: tenantId,
      p_title: groupTitle.trim(),
      p_member_ids: groupMembers,
      p_kind: 'group',
      p_location_key: null,
    });
    if (groupError) {
      setError(groupError.message);
      return;
    }
    setGroupTitle('');
    setGroupMembers([]);
    setGroupMode(false);
    setNewChatOpen(false);
    await loadConversations();
    if (typeof data === 'string') {
      setActiveConversationId(data);
      setMobileConversationOpen(true);
    }
  };

  const openConversation = (conversationId: string) => {
    setActiveConversationId(conversationId);
    setMobileConversationOpen(true);
  };

  const togglePin = async () => {
    if (!user || !activeConversation) return;
    const next = activeConversation.myMembership.pinned_at ? null : new Date().toISOString();
    const { error: pinError } = await supabase
      .from('chat_members')
      .update({ pinned_at: next })
      .eq('conversation_id', activeConversation.id)
      .eq('user_id', user.id);
    if (pinError) setError(pinError.message);
    else loadConversations().catch(console.error);
  };

  const openRecord = (message: MessageRow) => {
    if (!message.record_path) return;
    if (/^https?:\/\//i.test(message.record_path)) {
      window.open(message.record_path, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!chatRuntimeConfig.powderflowAppUrl) return;
    const base = chatRuntimeConfig.powderflowAppUrl.replace(/\/$/, '');
    const path = message.record_path.startsWith('/') ? message.record_path : `/${message.record_path}`;
    window.open(base + path, '_blank', 'noopener,noreferrer');
  };

  if (!authReady) {
    return <main className="loading-page"><div className="loading-orb">PF</div><span>Opening PowderFlow Chat…</span></main>;
  }

  if (!user) return <AuthScreen />;

  if (tenants.length === 0) {
    return (
      <main className="empty-page">
        <div className="empty-card">
          <div className="brand-mark">PF</div>
          <h1>No PowderFlow organisation assigned</h1>
          <p>Your account is valid, but it does not currently belong to a PowderFlow organisation.</p>
          <button className="secondary-button" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </main>
    );
  }

  return (
    <main className={`chat-page ${nudgePulse ? 'nudge-pulse' : ''}`}>
      <section className="chat-shell">
        <aside className={`sidebar ${mobileConversationOpen ? 'mobile-hidden' : ''}`}>
          <header className="sidebar-brand">
            <div className="brand-mark small">PF</div>
            <div className="brand-name">
              <strong>PowderFlow</strong>
              <span>Chat</span>
            </div>
            <button className="icon-button dark" aria-label="New chat" onClick={() => setNewChatOpen(true)}>
              <MessageSquarePlus size={19} />
            </button>
          </header>

          <div className="identity-card">
            <div className="avatar-wrap">
              <div className="avatar me">{initials(personMap.get(user.id)?.display_name ?? user.email ?? 'Me')}</div>
              <span className={`presence-dot ${manualStatus === 'appear_offline' ? 'offline' : manualStatus}`} />
            </div>
            <div className="identity-copy">
              <strong>{personMap.get(user.id)?.display_name ?? user.email}</strong>
              <label className="status-select">
                <select value={manualStatus} onChange={(event) => changeStatus(event.target.value as ManualStatus)}>
                  {Object.entries(STATUS_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <ChevronDown size={12} />
              </label>
            </div>
            <button className="icon-button dark" aria-label="Account options">
              <MoreHorizontal size={18} />
            </button>
          </div>

          {tenants.length > 1 && (
            <label className="tenant-picker">
              <span>Organisation</span>
              <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
                {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
              </select>
            </label>
          )}

          <div className="search-box dark-search">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search chats or people" />
            {search && <button onClick={() => setSearch('')}><X size={14} /></button>}
          </div>

          <div className="sidebar-tabs">
            <button className="active"><MessageCircleMore size={15} /> Chats</button>
            <button onClick={() => setNewChatOpen(true)}><Users size={15} /> People</button>
          </div>

          <div className="conversation-list">
            {filteredConversations.map((conversation) => {
              const onlineStatus = conversation.otherUserId ? presence[conversation.otherUserId] : undefined;
              return (
                <button
                  key={conversation.id}
                  className={`conversation-row ${conversation.id === activeConversationId ? 'active' : ''}`}
                  onClick={() => openConversation(conversation.id)}
                >
                  <div className="avatar-wrap">
                    <div className={`avatar ${conversation.kind !== 'direct' ? 'group' : ''}`}>
                      {conversation.initials}
                    </div>
                    {conversation.kind === 'direct' ? (
                      <span className={`presence-dot ${onlineStatus ?? 'offline'}`} />
                    ) : (
                      <span className="group-dot"><Hash size={9} /></span>
                    )}
                  </div>
                  <div className="conversation-copy">
                    <div className="conversation-topline">
                      <strong>{conversation.displayName}</strong>
                      <span>{timeLabel(conversation.lastMessage?.created_at ?? conversation.last_message_at)}</span>
                    </div>
                    <div className="conversation-preview">
                      <span>
                        {conversation.lastMessage?.message_type === 'nudge'
                          ? '⚡ Nudge'
                          : conversation.lastMessage?.message_type === 'attachment'
                            ? '📎 ' + (conversation.lastMessage.body || 'Attachment')
                            : conversation.lastMessage?.body || 'Start a conversation'}
                      </span>
                      {conversation.unreadCount > 0 && <b>{conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}</b>}
                    </div>
                    {conversation.location_key && <small>{conversation.location_key}</small>}
                  </div>
                </button>
              );
            })}
            {filteredConversations.length === 0 && (
              <div className="sidebar-empty">
                <MessageCircleMore size={25} />
                <span>No conversations yet</span>
                <button onClick={() => setNewChatOpen(true)}>Start one</button>
              </div>
            )}
          </div>

          <footer className="sidebar-footer">
            <span>{activeTenant?.name ?? 'PowderFlow'}</span>
            <button onClick={() => supabase.auth.signOut()}><LogOut size={15} /> Sign out</button>
          </footer>
        </aside>

        <section className={`conversation-pane ${mobileConversationOpen ? 'mobile-open' : ''}`}>
          {activeConversation ? (
            <>
              <header className="conversation-header">
                <button className="icon-button mobile-back" onClick={() => setMobileConversationOpen(false)} aria-label="Back">
                  <ArrowLeft size={20} />
                </button>
                <div className="avatar-wrap">
                  <div className={`avatar header-avatar ${activeConversation.kind !== 'direct' ? 'group' : ''}`}>
                    {activeConversation.initials}
                  </div>
                  {activeConversation.kind === 'direct' && (
                    <span className={`presence-dot ${activeConversation.otherUserId ? presence[activeConversation.otherUserId] ?? 'offline' : 'offline'}`} />
                  )}
                </div>
                <div className="header-copy">
                  <strong>{activeConversation.displayName}</strong>
                  <span>
                    {activeConversation.kind === 'direct'
                      ? STATUS_LABEL[(activeConversation.otherUserId && presence[activeConversation.otherUserId]) || 'appear_offline']
                      : `${activeConversation.members.length} members`}
                  </span>
                </div>
                <div className="header-actions">
                  <button className="icon-button" onClick={togglePin} title="Pin chat"><Zap size={18} /></button>
                  <button className="icon-button" title="Conversation settings"><Settings size={18} /></button>
                </div>
              </header>

              <div className="linked-banner">
                <span><Zap size={14} /> PowderFlow linked chat</span>
                <p>Order and job references open back in PowderFlow.</p>
              </div>

              {error && (
                <div className="error-banner">
                  <span>{error}</span>
                  <button onClick={() => setError('')}><X size={14} /></button>
                </div>
              )}

              <div className="message-area">
                {messages.length === 0 ? (
                  <div className="message-empty">
                    <div className="empty-bubble"><MessageCircleMore size={30} /></div>
                    <h2>Start the conversation</h2>
                    <p>Messages here are the same messages shown inside PowderFlow.</p>
                  </div>
                ) : (
                  messages.map((message, index) => {
                    const mine = message.sender_id === user.id;
                    const sender = personMap.get(message.sender_id);
                    const showDay = index === 0 || dayLabel(messages[index - 1].created_at) !== dayLabel(message.created_at);
                    const messageAttachments = attachments.filter((attachment) => attachment.message_id === message.id);
                    const messageReactions = reactions.filter((reaction) => reaction.message_id === message.id);
                    const reactionGroups = Array.from(new Set(messageReactions.map((reaction) => reaction.emoji)));
                    return (
                      <div key={message.id}>
                        {showDay && <div className="day-divider"><span>{dayLabel(message.created_at)}</span></div>}
                        <div className={`message-row ${mine ? 'mine' : ''}`}>
                          {!mine && <div className="avatar message-avatar">{initials(sender?.display_name ?? 'PF')}</div>}
                          <div className="message-stack">
                            {!mine && <span className="message-author">{sender?.display_name ?? 'PowderFlow user'}</span>}
                            <div className={`message-bubble ${message.message_type === 'nudge' ? 'nudge-message' : ''}`}>
                              {message.record_label && (
                                <button
                                  className="record-chip"
                                  onClick={() => openRecord(message)}
                                  disabled={!message.record_path || (!/^https?:\/\//i.test(message.record_path) && !chatRuntimeConfig.powderflowAppUrl)}
                                >
                                  <FileText size={13} /> {message.record_label}
                                </button>
                              )}
                              {message.message_type === 'nudge' ? (
                                <div className="nudge-content"><BellRing size={18} /> <strong>Nudge!</strong></div>
                              ) : (
                                message.body && <p>{message.body}</p>
                              )}
                              {messageAttachments.map((attachment) => (
                                <button key={attachment.id} className="attachment-card" onClick={() => openAttachment(attachment)}>
                                  {attachment.mime_type?.startsWith('image/') ? <Image size={18} /> : <Paperclip size={18} />}
                                  <span>
                                    <strong>{attachment.file_name}</strong>
                                    {attachment.size_bytes && <small>{Math.ceil(attachment.size_bytes / 1024)} KB</small>}
                                  </span>
                                </button>
                              ))}
                            </div>
                            <div className="message-meta">
                              <span>{timeLabel(message.created_at)}</span>
                              {mine && <span><Check size={10} /> Sent</span>}
                              {reactionGroups.map((emoji) => (
                                <button key={emoji} className="reaction-pill" onClick={() => reactTo(message.id, emoji)}>
                                  {emoji} {messageReactions.filter((reaction) => reaction.emoji === emoji).length}
                                </button>
                              ))}
                              <button className="reaction-add" onClick={() => reactTo(message.id, '👍')}>+</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="composer">
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadAttachment(file);
                    event.currentTarget.value = '';
                  }}
                />
                <button className="icon-button composer-tool" onClick={() => fileInputRef.current?.click()} title="Attach file">
                  <Paperclip size={18} />
                </button>
                <button className="icon-button composer-tool" title="Mention"><AtSign size={18} /></button>
                <input
                  className="composer-input"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder={`Message ${activeConversation.displayName}`}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button className="icon-button composer-tool" title="Emoji"><Smile size={18} /></button>
                <button className="nudge-button" onClick={() => sendMessage('nudge')} disabled={sending} title="Send a nudge">
                  <BellRing size={17} />
                </button>
                <button className="send-button" onClick={() => sendMessage()} disabled={sending || !draft.trim()} aria-label="Send">
                  <Send size={17} />
                </button>
              </div>
            </>
          ) : (
            <div className="no-active-chat">
              <div className="no-active-logo"><MessageCircleMore size={34} /></div>
              <h1>PowderFlow Chat</h1>
              <p>Choose a conversation or start a new one.</p>
              <button className="primary-button" onClick={() => setNewChatOpen(true)}><Plus size={16} /> New chat</button>
            </div>
          )}
        </section>

        <aside className="details-pane">
          {activeConversation && (
            <>
              <div className="details-hero">
                <div className={`avatar detail-avatar ${activeConversation.kind !== 'direct' ? 'group' : ''}`}>
                  {activeConversation.initials}
                </div>
                <h2>{activeConversation.displayName}</h2>
                <p>{activeConversation.location_key || (activeConversation.kind === 'direct' ? personMap.get(activeConversation.otherUserId ?? '')?.primary_location : 'PowderFlow group')}</p>
                <span className="details-presence">
                  {activeConversation.kind === 'direct' ? (
                    <>
                      <span className={`presence-dot static ${activeConversation.otherUserId ? presence[activeConversation.otherUserId] ?? 'offline' : 'offline'}`} />
                      {STATUS_LABEL[(activeConversation.otherUserId && presence[activeConversation.otherUserId]) || 'appear_offline']}
                    </>
                  ) : (
                    <><Users size={13} /> {activeConversation.members.length} members</>
                  )}
                </span>
              </div>
              <div className="detail-section">
                <strong>PowderFlow links</strong>
                <button><span>Jobs & orders mentioned</span></button>
                <button><span>Files & photos</span><b>{attachments.length}</b></button>
              </div>
              <div className="detail-section">
                <strong>Chat actions</strong>
                <button onClick={togglePin}><span>{activeConversation.myMembership.pinned_at ? 'Unpin conversation' : 'Pin conversation'}</span></button>
                <button><span>Mute notifications</span></button>
                <button><span>Search in chat</span></button>
              </div>
              <div className="integration-card">
                <Zap size={18} />
                <div>
                  <strong>One conversation everywhere</strong>
                  <p>This chat is stored in PowderFlow and is shared with the internal messenger.</p>
                </div>
              </div>
            </>
          )}
        </aside>
      </section>

      {newChatOpen && (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setNewChatOpen(false)}>
          <section className="new-chat-modal">
            <header>
              <div>
                <span className="eyebrow">NEW CONVERSATION</span>
                <h2>{groupMode ? 'Create a group' : 'Message someone'}</h2>
              </div>
              <button className="icon-button" onClick={() => setNewChatOpen(false)}><X size={18} /></button>
            </header>
            <div className="mode-switch">
              <button className={!groupMode ? 'active' : ''} onClick={() => setGroupMode(false)}>Direct</button>
              <button className={groupMode ? 'active' : ''} onClick={() => setGroupMode(true)}>Group</button>
            </div>
            {groupMode && (
              <label className="group-title">
                Group name
                <input value={groupTitle} onChange={(event) => setGroupTitle(event.target.value)} placeholder="e.g. Production • All Locations" />
              </label>
            )}
            <div className="search-box modal-search">
              <Search size={16} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a PowderFlow user" />
            </div>
            <div className="people-list">
              {filteredPeople.map((person) => {
                const selected = groupMembers.includes(person.user_id);
                return (
                  <button
                    key={person.user_id}
                    onClick={() => {
                      if (!groupMode) startDirect(person.user_id);
                      else setGroupMembers((current) => selected ? current.filter((id) => id !== person.user_id) : [...current, person.user_id]);
                    }}
                  >
                    <div className="avatar-wrap">
                      <div className="avatar">{initials(person.display_name)}</div>
                      <span className={`presence-dot ${presence[person.user_id] ?? 'offline'}`} />
                    </div>
                    <div>
                      <strong>{person.display_name}</strong>
                      <span>{person.primary_location || 'PowderFlow'}</span>
                    </div>
                    {groupMode && <span className={`check-box ${selected ? 'selected' : ''}`}>{selected && <Check size={13} />}</span>}
                  </button>
                );
              })}
            </div>
            {groupMode && (
              <footer>
                <span>{groupMembers.length} selected</span>
                <button className="primary-button" onClick={createGroup} disabled={!groupTitle.trim()}>
                  <Users size={16} /> Create group
                </button>
              </footer>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
